import { uniq, uniqueId } from 'lodash';

enum ApiErrorEnum {
  NoToken = 'NoToken',
  Unauthorized = 'Unauthorized',
  Unknown = 'Unknown',
  UnreachableHost = 'UnreachableHost',
  BodyEncodingFailed = 'BodyEncodingFailed',
  ResponseDecodingFailed = 'ResponseDecodingFailed',
  RequestFailed = 'RequestFailed',
}

export type FrameStats = {
  avg_sentence_length: number;
  capture_duration_ms: number;
  compression_ratio: number;
  flesch_score: number;
  readability_score: number;
  text_size: number;
  zlib_quality: number;
};

export type TraversedConversationMessage = {
  content: string;
  message_type: string;
  sender: string;
  time_sent: string;
  timestring: string;
};

type TraversalDataFrame = {
  app: string;
  content: string;
  timestamp: string;
  type: string;
  window: string;
  frame_stats: FrameStats;
};

type TextTraversalDataFrame = {
  app: string;
  content: string;
  timestamp: string;
  type: 'text';
  window: string;
  frame_stats: FrameStats;
};

type BrowserTraversalDataFrame = {
  app: string;
  content: string;
  timestamp: string;
  type: 'browser';
  window: string;
  url: string;
  frame_stats: FrameStats;
};

type ConversationDataFrame = {
  app: string;
  is_group: boolean;
  channel: string;
  messages: TraversedConversationMessage[];
  timestamp: string;
  type: 'conversation';
  window: string;
  frame_stats: FrameStats;
};

type DocumentDataFrame = {
  app: string;
  window: string;
  type: 'document';
  timestamp: string;
  title: string;
  content: string;
  frame_stats: FrameStats;
};

type TraversalDataRequest = {
  collected: (
    | TraversalDataFrame
    | TextTraversalDataFrame
    | ConversationDataFrame
    | DocumentDataFrame
    | BrowserTraversalDataFrame
  )[];
};

export const isTraversalDataRequest = (
  data: any
): data is TraversalDataRequest => {
  return (
    data &&
    typeof data === 'object' &&
    data['requestBody'] &&
    'collected' in data['requestBody'] &&
    Array.isArray(data['requestBody']['collected'])
  );
};

export const isTextTraversalDataFrame = (
  data: any
): data is TextTraversalDataFrame => {
  return (
    data &&
    typeof data === 'object' &&
    'type' in data &&
    data['type'] === 'text'
  );
};

export const isBrowserTraversalDataFrame = (
  data: any
): data is BrowserTraversalDataFrame => {
  return (
    data &&
    typeof data === 'object' &&
    'type' in data &&
    data['type'] === 'browser'
  );
};

export const isConversationDataFrame = (
  data: any
): data is ConversationDataFrame => {
  return (
    data &&
    typeof data === 'object' &&
    'type' in data &&
    data['type'] === 'conversation'
  );
};

export const isDocumentDataFrame = (data: any): data is DocumentDataFrame => {
  return (
    data &&
    typeof data === 'object' &&
    'type' in data &&
    data['type'] === 'document'
  );
};

export type RawApiLog = {
  url: string;
  httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE';
  contentType:
    | 'application/json'
    | 'multipart/form-data'
    | 'application/x-www-form-urlencoded'
    | 'image/png';
  requestBody: Record<string, unknown> | TraversalDataRequest;
  responseBody: Record<string, unknown>;
  errorMessage: ApiErrorEnum | string;
  statusCode: number;
  timestamp: number;
};

type BaseApiLog = {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  contentType:
    | 'application/json'
    | 'multipart/form-data'
    | 'application/x-www-form-urlencoded'
    | 'image/png';

  responseBody: Record<string, unknown> | string;
  errorMessage: ApiErrorEnum | string;
  statusCode: number;
  timestamp: Date;
  requestId: string;
  app: string;
  tags: string[];
};

export type ApiLog =
  | (BaseApiLog & {
      isTraversalDataRequest: true;
      isConversationDataRequest: false;
      isTextTraversalDataFrame: false;
      isDocumentDataFrame: false;
      isBrowserTraversalDataFrame: false;
      requestBody: TraversalDataFrame;
    })
  | (BaseApiLog & {
      isTraversalDataRequest: false;
      isConversationDataRequest: false;
      isTextTraversalDataFrame: false;
      isDocumentDataFrame: false;
      isBrowserTraversalDataFrame: false;
      requestBody: Record<string, unknown>;
    })
  | (BaseApiLog & {
      isTraversalDataRequest: true;
      isConversationDataRequest: true;
      isTextTraversalDataFrame: false;
      isDocumentDataFrame: false;
      isBrowserTraversalDataFrame: false;
      requestBody: ConversationDataFrame;
    })
  | (BaseApiLog & {
      isTraversalDataRequest: true;
      isConversationDataRequest: false;
      isTextTraversalDataFrame: true;
      isDocumentDataFrame: false;
      isBrowserTraversalDataFrame: false;
      requestBody: TextTraversalDataFrame;
    })
  | (BaseApiLog & {
      isTraversalDataRequest: true;
      isConversationDataRequest: false;
      isTextTraversalDataFrame: false;
      isDocumentDataFrame: true;
      isBrowserTraversalDataFrame: false;
      requestBody: DocumentDataFrame;
    })
  | (BaseApiLog & {
      isTraversalDataRequest: true;
      isConversationDataRequest: false;
      isTextTraversalDataFrame: false;
      isDocumentDataFrame: false;
      isBrowserTraversalDataFrame: true;
      requestBody: BrowserTraversalDataFrame;
    });

const getTagsFromTraversalDataFrame = (frame: TraversalDataFrame): string[] => {
  return ['traversal', frame.type];
};

const getTagsFromBrowserTraversalDataFrame = (
  frame: BrowserTraversalDataFrame
): string[] => {
  const hostDomain = new URL(frame.url).hostname;
  return ['browser', hostDomain, frame.type];
};

const getTagsFromConversationDataFrame = (
  frame: ConversationDataFrame
): string[] => {
  return ['conversation', frame.channel, frame.is_group ? 'group' : 'dm'];
};

const getTagsFromDocumentDataFrame = (frame: DocumentDataFrame): string[] => {
  return ['document', frame.title];
};

const getTagsFromRawApiLog = (apiLog: RawApiLog): string[] => {
  const apiEndpoint = apiLog.url.split('/').pop() as string;
  const responseTag =
    apiLog.errorMessage || apiLog.statusCode === -1 ? 'error' : 'success';
  return [apiEndpoint, responseTag];
};

export const transformRawApiLogToApiLog = (apiLog: RawApiLog): ApiLog[] => {
  const jsonParsedApiLog = {
    ...apiLog,
    requestBody:
      apiLog.requestBody && JSON.parse(apiLog.requestBody as unknown as string),
    responseBody:
      apiLog.responseBody &&
      JSON.parse(apiLog.responseBody as unknown as string),
  };
  const commonTags = getTagsFromRawApiLog(jsonParsedApiLog);
  const isApiLogTraversalDataRequest = isTraversalDataRequest(jsonParsedApiLog);

  if (isApiLogTraversalDataRequest) {
    const traversalDataRequest = jsonParsedApiLog.requestBody;
    const sharedProperties = {
      url: jsonParsedApiLog.url,
      method: jsonParsedApiLog.httpMethod,
      contentType: jsonParsedApiLog.contentType,
      responseBody: jsonParsedApiLog.responseBody,
      errorMessage: jsonParsedApiLog.errorMessage,
      statusCode: jsonParsedApiLog.statusCode,
      timestamp: new Date(jsonParsedApiLog.timestamp),
      isTraversalDataRequest: true as const,
    };

    const textTraversalDataFrames = (
      traversalDataRequest as TraversalDataRequest
    ).collected
      .filter<TextTraversalDataFrame>(isTextTraversalDataFrame)
      .map((frame) => {
        return {
          requestId: uniqueId(),
          app: frame.app,
          tags: uniq([...commonTags, ...getTagsFromTraversalDataFrame(frame)]),
          isTextTraversalDataFrame: true as const,
          isConversationDataRequest: false as const,
          isDocumentDataFrame: false as const,
          isBrowserTraversalDataFrame: false as const,
          ...sharedProperties,
          requestBody: frame,
        };
      });

    const browserTraversalDataFrames = (
      traversalDataRequest as TraversalDataRequest
    ).collected
      .filter<BrowserTraversalDataFrame>(isBrowserTraversalDataFrame)
      .map((frame) => {
        return {
          requestId: uniqueId(),
          app: frame.app,
          tags: uniq([
            ...commonTags,
            ...getTagsFromBrowserTraversalDataFrame(frame),
          ]),
          isBrowserTraversalDataFrame: true as const,
          isTextTraversalDataFrame: false as const,
          isConversationDataRequest: false as const,
          isDocumentDataFrame: false as const,
          ...sharedProperties,
          requestBody: frame,
        };
      });
    const conversationDataFrames = (
      traversalDataRequest as TraversalDataRequest
    ).collected
      .filter<ConversationDataFrame>(isConversationDataFrame)
      .map((frame) => {
        return {
          requestId: uniqueId(),
          app: frame.app,
          tags: uniq([
            ...commonTags,
            ...getTagsFromConversationDataFrame(frame),
          ]),
          isConversationDataRequest: true as const,
          isTextTraversalDataFrame: false as const,
          isDocumentDataFrame: false as const,
          isBrowserTraversalDataFrame: false as const,
          requestBody: frame,
          ...sharedProperties,
        };
      });

    const documentDataFrames = (
      traversalDataRequest as TraversalDataRequest
    ).collected
      .filter<DocumentDataFrame>(isDocumentDataFrame)
      .map((frame) => {
        return {
          requestId: uniqueId(),
          app: frame.app,
          tags: uniq([...commonTags, ...getTagsFromDocumentDataFrame(frame)]),
          isConversationDataRequest: false as const,
          isTextTraversalDataFrame: false as const,
          isDocumentDataFrame: true as const,
          requestBody: frame,
          isBrowserTraversalDataFrame: false as const,
          ...sharedProperties,
        };
      });

    return [
      ...textTraversalDataFrames,
      ...browserTraversalDataFrames,
      ...conversationDataFrames,
      ...documentDataFrames,
    ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }
  return [
    {
      app: 'unavailable',
      method: jsonParsedApiLog.httpMethod,
      contentType: jsonParsedApiLog.contentType,
      responseBody: jsonParsedApiLog.responseBody,
      errorMessage: jsonParsedApiLog.errorMessage,
      statusCode: jsonParsedApiLog.statusCode,
      timestamp: new Date(jsonParsedApiLog.timestamp),
      requestId: uniqueId(),
      tags: commonTags,
      isTraversalDataRequest: false,
      isTextTraversalDataFrame: false,
      isConversationDataRequest: false,
      isDocumentDataFrame: false,
      isBrowserTraversalDataFrame: false,
      requestBody: jsonParsedApiLog.requestBody as Record<string, unknown>,
      url: jsonParsedApiLog.url,
    },
  ];
};
